import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { portalApi } from '../services/portalApi';
import { queryKeys } from './queryKeys';
import { useApiMutation } from '../hooks/useApiMutation';

const patchVendor = (id, patch) => (v) => (v._id === id ? { ...v, ...patch } : v);

const useVendorMutation = (opts) => useApiMutation({ invalidate: [queryKeys.vendors.all, queryKeys.contacts.all], ...opts });

export function useVendors({ status = 'all', trade = '', search = '', sort = 'company', page = 1, pageSize = 10 } = {}) {
  const params = {
    page,
    limit: pageSize,
    sort,
    status: status === 'all' ? undefined : status,
    trade: trade || undefined,
    search: search || undefined
  };
  const query = useQuery({
    queryKey: queryKeys.vendors.list(params),
    queryFn: () => portalApi.vendors(params),
    placeholderData: keepPreviousData
  });
  return {
    rows: query.data?.docs || [],
    total: query.data?.totalDocs || 0,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error
  };
}

export function useVendorOptions() {
  const params = { limit: 100, sort: 'company' };
  return useQuery({
    queryKey: queryKeys.vendors.list(params),
    queryFn: () => portalApi.vendors(params),
    select: (data) => data.docs || [],
    staleTime: 5 * 60_000
  });
}

export function useVendorSummary() {
  return useQuery({
    queryKey: queryKeys.vendors.summary(),
    queryFn: portalApi.vendorsSummary,
    staleTime: 60_000
  });
}

export function useVendorQuery(id) {
  return useQuery({
    queryKey: queryKeys.vendors.detail(id),
    queryFn: () => portalApi.vendor(id),
    enabled: Boolean(id)
  });
}

export function useCreateVendor() {
  return useVendorMutation({
    mutationFn: (values) => portalApi.createVendor(values),
    success: (data) => `${data.vendor.company} added`,
    error: 'Could not add this vendor'
  });
}

export function useUpdateVendor() {
  return useVendorMutation({
    mutationFn: ({ _id, ...values }) => portalApi.updateVendor(_id, values),
    optimistic: ({ _id, ...values }) => [
      { key: queryKeys.vendors.lists(), update: (data) => ({ ...data, docs: data.docs.map(patchVendor(_id, values)) }) },
      { key: queryKeys.vendors.detail(_id), update: (data) => ({ ...data, vendor: { ...data.vendor, ...values } }) }
    ],
    success: 'Vendor updated',
    error: 'Could not update this vendor'
  });
}

export function useDeleteVendor() {
  return useVendorMutation({
    mutationFn: (id) => portalApi.removeVendor(id),
    optimistic: (id) => [
      {
        key: queryKeys.vendors.lists(),
        update: (data) => ({
          ...data,
          docs: data.docs.filter((v) => v._id !== id),
          totalDocs: Math.max(0, (data.totalDocs || 0) - 1)
        })
      }
    ],
    success: 'Vendor removed',
    error: 'Could not remove this vendor'
  });
}
